import { useSyncExternalStore } from "react";
import type { TLRichText, TLShapeId } from "@tldraw/tlschema";
import type { Editor } from "./canvas";
import { api, notify } from "./api";
import { agentMentionsIn } from "../shared/mentions";
import type { DraftSummary } from "../shared/agent-drafts";

/*
 * @Agent in comments. The agent panel already polls the caller's
 * conversations; it publishes them here so a comment thread can show its
 * agent working without a second poll. Others see the run only through the
 * replies the harness posts in the thread.
 */
type AgentThreads = {
  /** The agents feature is on and the viewer can edit this project. */
  enabled: boolean;
  endpoint: string;
  list: DraftSummary[];
};
let state: AgentThreads = { enabled: false, endpoint: "", list: [] };
const listeners = new Set<() => void>();
export function publishAgentThreads(next: AgentThreads) {
  state = next;
  for (const listener of listeners) listener();
}
export function useAgentThreads() {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => state,
  );
}
/** Whether this viewer can call the agent from a comment right now. */
export const agentsCallable = () => state.enabled;
export function threadConversation(list: DraftSummary[], threadId: string) {
  return list.find((d) => d.threadId === threadId && !d.deletedAt);
}

/** Whether posting this body should reach the agent: a new @Agent, or an answer to its question. */
export function callsAgent(
  body: TLRichText,
  threadId: string,
  previous?: TLRichText,
) {
  if (!state.enabled) return false;
  const before = new Set(previous ? agentMentionsIn(previous) : []);
  if (agentMentionsIn(body).some((id) => !before.has(id))) return true;
  return (
    !previous &&
    threadConversation(state.list, threadId)?.status === "waiting"
  );
}

/**
 * Ask the server to run the agent for a posted comment. The comment travels
 * over sync first, so an "unavailable" answer is retried briefly until the
 * board has it.
 */
export async function callAgent(threadId: string, commentId: string) {
  const { endpoint } = state;
  for (let attempt = 0; ; attempt++) {
    try {
      await api(`${endpoint}/comment`, { threadId, commentId });
      return;
    } catch (error) {
      const message = (error as Error).message;
      if (attempt < 8 && /unavailable/i.test(message)) {
        await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
        continue;
      }
      notify(message);
      return;
    }
  }
}

export async function reverseFromThread(
  conversationId: string,
  batchId: string,
  redo: boolean,
) {
  try {
    await api(`${state.endpoint}/${conversationId}/thread-undo`, {
      batchId,
      redo,
    });
  } catch (error) {
    notify((error as Error).message);
  }
}

/** Select and frame an object the agent changed. */
export function jumpToShape(editor: Editor, id: string) {
  const shape = editor.getShape(id as TLShapeId);
  if (!shape) {
    notify("That object is no longer on the canvas.");
    return;
  }
  editor.setCurrentPage(editor.getAncestorPageId(shape)!);
  editor.select(shape.id);
  const bounds = editor.getShapePageBounds(shape);
  if (bounds)
    editor.zoomToBounds(bounds, {
      targetZoom: Math.min(1, editor.getZoomLevel()),
      immediate: true,
    });
}
